'use client';

import {
  IconDefinition,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { Image, Logo } from '@nettbureau/featured-core';
import { HStack, IconButton, useCss } from '@nettbureau/vibrant-ui';
import { useContext, useState } from 'react';
import { ConfigContext } from '../provider/config-provider';
import { getTheme, getTokenFromVibrant } from '../theme';

export interface CardProps {
  id?: number;
  icon: Image | null;
  center: Image | null;
  background: Image | Image[] | null;
  logo: Logo['logo'];
  height?: number;
}

interface StepperIconProps {
  icon: IconDefinition;
  onPress: () => void;
}

export function CardBackground({
  background,
  logo,
  icon,
  center,
  id,
  height,
}: CardProps) {
  const { globalThemeVariant, theme } = useContext(ConfigContext);
  const isMultipleBackgrounds = Array.isArray(background);
  const [backgroundIndex, setBackgroundIndex] = useState(0);

  const logoSize = logo?.variant ?? 'Small';
  const logoPosition = logo?.position ?? 'Middle';

  const cardHeaderContainerStyles = useCss(({ theme }) => {
    return {
      marginBottom: theme.scales.base,
      height: height ? height : 250,
      scrollMargin: 40,
      width: '100%',
      display: 'block',
    };
  });

  const logoStyles = useCss(({ theme }) => {
    return {
      borderRadius: theme.borderRadiuses.lg,
      height: 50,
      width: determineLogoSize(logoSize),
    };
  });

  const logoContainerStyles = useCss(({ theme }) => {
    return {
      background: '#fff',
      position: 'relative',
      borderRadius: theme.borderRadiuses.lg,
      width: determineLogoSize(logoSize),
      height: 50,
      top: '-25px',
      left: calculateLeftOffset(logoPosition, logoSize),
    };
  });

  const backgroundStyles = useCss((input) => {
    const backgroundUrl = isMultipleBackgrounds
      ? background[backgroundIndex]?.url
      : background?.url;

    const defaultBackgroundStyle = getTokenFromVibrant(
      globalThemeVariant === 'default'
        ? getTheme(theme).default.card.cardHeaderBackground
        : getTheme(theme).secondary.card.cardHeaderBackground,
      input.theme,
    );

    return {
      background: backgroundUrl
        ? `url(${backgroundUrl})`
        : defaultBackgroundStyle,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      borderTopLeftRadius: input.theme.borderRadiuses.lg,
      borderTopRightRadius: input.theme.borderRadiuses.lg,
      width: '100%',
      height: 200,
      position: 'relative',
      '@media(max-width: 660px)': {
        borderTopLeftRadius: input.theme.borderRadiuses.lg,
        borderTopRightRadius: input.theme.borderRadiuses.lg,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
    };
  });

  const iconStyles = useCss({
    height: 40,
    width: 40,
    top: '10px',
    position: 'absolute',
    right: '10px',
  });

  const centerStyles = useCss({
    position: 'absolute',
    maxHeight: '100%',
    objectFit: 'cover',
    margin: 'auto',
    borderRadius: '50%',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    padding: 28,
  });

  const StepperIcon = ({ icon, onPress }: StepperIconProps) => (
    <IconButton
      icon={icon}
      color="neutral"
      variant="elevated"
      css={{ opacity: 0.65 }}
      size="sm"
      onPress={onPress}
    />
  );

  return (
    <div css={cardHeaderContainerStyles} id={`${id}`}>
      <div css={backgroundStyles}>
        {isMultipleBackgrounds && background.length > 1 && (
          <HStack
            width="100%"
            height="100%"
            align="center"
            justify="space-between"
          >
            <StepperIcon
              icon={faChevronLeft}
              onPress={() =>
                setBackgroundIndex(
                  (backgroundIndex - 1 + background.length) % background.length,
                )
              }
            />
            <StepperIcon
              icon={faChevronRight}
              onPress={() =>
                setBackgroundIndex((backgroundIndex + 1) % background.length)
              }
            />
          </HStack>
        )}
        {center && (
          <img
            alt={center.alt ?? ''}
            draggable={false}
            css={centerStyles}
            src={center.url}
          />
        )}
        {icon && (
          <img
            alt={icon.alt ?? ''}
            draggable={false}
            css={iconStyles}
            src={icon.url}
          />
        )}
      </div>
      {logo && (
        <div css={logoContainerStyles}>
          <img
            alt={logo?.image.alt ?? ''}
            draggable={false}
            css={logoStyles}
            src={logo?.image.url}
          />
        </div>
      )}
    </div>
  );
}

export type LogoPosition = 'Left' | 'Right' | 'Middle';

function calculateLeftOffset(position: LogoPosition, size: LogoSize) {
  const width = determineLogoSize(size);

  switch (position) {
    case 'Middle':
      return `calc(50% - ${width / 2}px)`;
    case 'Left':
      return '10%';
    case 'Right':
      return `calc(90% - ${width}px)`;
  }
}

export function determineLogoSize(size: LogoSize) {
  switch (size) {
    case 'Large':
      return 100;
    case 'Small':
      return 50;
  }
}

export type LogoSize = 'Small' | 'Large';
